import ApplicationController from './application_controller'

/**
 * Inline Edit Controller
 *
 * Companion to the `reveal` controller for click-to-edit inline editing.
 * The `reveal` controller handles toggling display/editor visibility.
 * This controller adds click-outside-to-close and auto-focus on open.
 *
 * Wire up with `reveal` on the same element:
 *
 *   <div data-controller="inline-edit reveal"
 *        data-action="click@document->inline-edit#closeOnClickOutside">
 *     <span data-reveal-target="item">
 *       Display value
 *       <button data-action="click->reveal#toggle click->inline-edit#focus">Edit</button>
 *     </span>
 *     <div data-reveal-target="item" data-inline-edit-target="editor" class="hidden">
 *       <form>...</form>
 *     </div>
 *   </div>
 *
 * Targets:
 *   editor - The editor element (for state checks and auto-focus)
 *
 * Actions:
 *   focus               - Auto-focuses the first input/select in the editor
 *   closeOnClickOutside - Closes the editor when clicking outside
 */
export default class extends ApplicationController {
  static targets = ['editor']

  focus() {
    // Run after reveal#toggle has shown the editor (next frame)
    requestAnimationFrame(() => {
      if (this.#isEditing) {
        const focusable = this.editorTarget.querySelector('select, input')
        focusable?.focus()
        this.addBreadcrumb('UI', 'inline_edit_open')
      }
    })
  }

  closeOnClickOutside(event) {
    if (!this.#isEditing) return
    if (this.element.contains(event.target)) return

    // Toggle reveal items to close editor and show display
    this.#toggleReveal()
    this.addBreadcrumb('UI', 'inline_edit_close')
  }

  get #isEditing() {
    return this.hasEditorTarget && !this.editorTarget.classList.contains('hidden')
  }

  #toggleReveal() {
    // Find the reveal controller on the same element and call toggle
    const revealController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'reveal',
    )
    if (revealController) {
      revealController.toggle()
    }
  }
}
